import { queryClient } from '~/app/providers/http'
import { TSessionDetails } from '~/features/auth/models'
import { queries } from '~/features/auth/utils'
import { EHttpService } from '~/models/api'
import EEnvironmentVariables from '~/models/config'
import AmplifyHttpService from '~/services/http/amplifyHttpService'
import AxiosHttpService from '~/services/http/axiosHttpService'
import HttpServiceBase, { HttpRequestBase } from '~/services/http/models'

class HttpService {
  private static partnerServiceInstance = new AxiosHttpService(
    EHttpService.partner,
    import.meta.env[EEnvironmentVariables.VITE_PARTNER_SERVICE] || '',
  )

  private static campaignsServiceInstance = new AxiosHttpService(
    EHttpService.campaigns,
    import.meta.env[EEnvironmentVariables.VITE_CAMPAIGN_SERVICE] || '',
  )

  private static scenariosServiceInstance = new AxiosHttpService(
    EHttpService.scenarios,
    import.meta.env[EEnvironmentVariables.VITE_SCENARIO_SERVICE] || '',
  )

  private static agentServiceInstance = new AxiosHttpService(
    EHttpService.agent,
    import.meta.env[EEnvironmentVariables.VITE_AGENT_SERVICE] || '',
  )

  private static async updateAccessTokens() {
    const authSession = (await queryClient.ensureQueryData(
      queries.authSession(),
    )) as TSessionDetails | undefined
    const accessToken = authSession?.accessToken

    this.partnerServiceInstance.updateToken(accessToken)
    this.campaignsServiceInstance.updateToken(accessToken)
    this.scenariosServiceInstance.updateToken(accessToken)
    this.agentServiceInstance.updateToken(accessToken)
  }

  private static amplifyServiceInstance: HttpServiceBase =
    new AmplifyHttpService()

  private static async getInstance(
    service: EHttpService,
  ): Promise<HttpServiceBase | AxiosHttpService> {
    switch (service) {
      case EHttpService.partner:
        return this.partnerServiceInstance

      case EHttpService.campaigns:
        return this.campaignsServiceInstance

      case EHttpService.scenarios:
        return this.scenariosServiceInstance

      case EHttpService.agent:
        return this.agentServiceInstance

      default:
        return this.amplifyServiceInstance
    }
  }

  public static async getAllAvailableInterceptors() {
    return Promise.all([
      this.partnerServiceInstance,
      this.campaignsServiceInstance,
      this.agentServiceInstance,
    ]).then((instances) => {
      return instances.map((instance) => instance.getInterceptors())
    })
  }

  public static async get<T>(
    { path, query }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    await this.updateAccessTokens()
    return (await HttpService.getInstance(service)).get<T>({ path, query })
  }

  public static async post<T>(
    { path, query, payload }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    await this.updateAccessTokens()
    return (await HttpService.getInstance(service)).post<T>({
      path,
      query,
      payload,
    })
  }

  public static async put<T>(
    { path, query, payload }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    await this.updateAccessTokens()
    return (await HttpService.getInstance(service)).put<T>({
      path,
      query,
      payload,
    })
  }

  public static async patch<T>(
    { path, query, payload }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    await this.updateAccessTokens()
    return (await HttpService.getInstance(service)).patch<T>({
      path,
      query,
      payload,
    })
  }

  public static async delete<T>(
    { path, query, payload }: HttpRequestBase,
    service: EHttpService = EHttpService.amplify,
  ) {
    await this.updateAccessTokens()
    return (await HttpService.getInstance(service)).delete<T>({
      path,
      query,
      payload,
    })
  }
}

export default HttpService
